import { useCallback } from 'react';

import { attendanceService, type MonthlyCutsResult } from '@/api/domains/attendance';
import type { ApiResponse } from '@/api/shared';


export interface MonthlyCutDateRange {
  from: string;
  to: string;
}

export interface MonthlyCutDateRangeLookupOutcome {
  range: MonthlyCutDateRange | null;
  // Populated only when getMonthlyCutsNew itself failed, so callers can tell a genuine
  // backend error (report via showApiError) apart from a "cut not found" outcome (no error
  // to report — response.success is true, the id just isn't in the returned list).
  response: ApiResponse<MonthlyCutsResult> | null;
}

export interface UseMonthlyCutDateRangeLookupResult {
  resolveDateRange: (
    companyId: string,
    monthlyCutId: string,
  ) => Promise<MonthlyCutDateRangeLookupOutcome>;
}

// MonthlyCut.startDate/endDate come pre-formatted as dd/mm/yyyy (see monthlyCuts.mapper.ts),
// while the plain date-range screens this resolves for (Mis registros de asistencia,
// Resumen ausentismos) expect yyyy-mm-dd (formatDateToYYYYMMDD's output format).
function toIsoDate(ddMmYyyy: string): string | null {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(ddMmYyyy);
  if (!match) return null;
  const [, day, month, year] = match;
  return `${year}-${month}-${day}`;
}

// Resolves the concrete date range of a monthly cut on demand. The dashboard's own cut
// selector (useMonthSummaryFilters) only knows monthlyCutId/year/month — LC_AllMonthlyCutoffItemOut
// has no dates — so "Detalle" navigation hooks that land on a plain date-range screen (as
// opposed to a screen that already understands "corte mensual", like Resumen asistencia)
// resolve the range here, at click time, instead of adding an always-on monthly-cuts fetch
// to those destination screens.
export function useMonthlyCutDateRangeLookup(): UseMonthlyCutDateRangeLookupResult {
  const resolveDateRange = useCallback(async (
    companyId: string,
    monthlyCutId: string,
  ): Promise<MonthlyCutDateRangeLookupOutcome> => {
    const response = await attendanceService.getMonthlyCutsNew({
      filterCompanyId: Number(companyId),
    });
    if (!response.success || !response.data) return { range: null, response };

    const matchedCut = response.data.cuts.find((cut) => cut.monthlyCutId === Number(monthlyCutId));
    if (!matchedCut) return { range: null, response: null };

    const from = toIsoDate(matchedCut.startDate);
    const to = toIsoDate(matchedCut.endDate);
    if (!from || !to) return { range: null, response: null };

    return { range: { from, to }, response: null };
  }, []);

  return { resolveDateRange };
}

export default useMonthlyCutDateRangeLookup;
